import { useEffect, useRef, useState } from 'react';
import { api, ApiError } from '../lib/api';
import { useToast } from '../lib/toast-context';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import {
  FIELD_LABEL_CLASS,
  FIELD_INPUT_CLASS,
  FIELD_HINT_CLASS,
} from '../components/ui/form-field';
import { ChangePasswordForm } from '../components/ChangePasswordForm';
import { validateDisplayName, DISPLAY_NAME_MAX } from '../../shared/profile-validation';

/**
 * ToolsProfileForm — tools-eigene, schlanke Profil-Form (P1c PR-B, D-P1c-13).
 *
 * Bewusste Kurzzeit-Duplikation der DAM-`ProfilePage`: die DAM-Version hängt an
 * `useLayoutUser` / `AUTH_USER_CHANGED` / `startTour` (alle DAM-Layout-gebunden) →
 * kein Verbatim-Move. Kern übernommen (shared `profile-validation`, Single-flight,
 * `PATCH /api/auth/me`), aber:
 *  - `user` kommt per Props (kein Layout-Context).
 *  - Erfolg → `onSaved(displayName)`-Callback (tools-lokaler State-Refresh in
 *    ToolsApp) statt des DAM-`AUTH_USER_CHANGED`-Events (nur in DAM-App.tsx gelauscht).
 *  - KEINE „Tour neu starten"-Card (driver.js ist DAM-UX).
 * Das DAM-Original stirbt in PR-C → kein Shared-Refactor (danach EIN Konsument).
 */
interface ToolsProfileFormProps {
  user: { email: string; displayName: string; role: string };
  onSaved: (displayName: string) => void;
}

export function ToolsProfileForm({ user, onSaved }: ToolsProfileFormProps) {
  const toast = useToast();
  const [draft, setDraft] = useState(user.displayName);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Single-flight gegen Doppel-Submit trotz disabled-Button (schneller
  // Enter-Anschlag kann in der Commit-Phase zweimal dispatchen).
  const inflight = useRef(false);

  const trimmed = draft.trim();
  const validation = validateDisplayName(draft);
  const isValid = validation.ok;
  const isDirty = trimmed !== user.displayName;
  const canSave = !saving && isValid && isDirty;

  // Externe user-Änderung (z.B. anderer Tab): draft nur re-syncen wenn pristine,
  // nie eine laufende Bearbeitung überschreiben.
  useEffect(() => {
    if (!isDirty) setDraft(user.displayName);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.displayName]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSave || inflight.current) return;
    // `validateDisplayName` + der PATCH-Handler normalisieren auf NFC. Den
    // NORMALISIERTEN Wert senden UND lokal speichern — sonst divergiert der
    // Client-State bei NFD-Eingaben (z.B. macOS-Paste) bis zum Reload (Codex-PR F).
    const validated = validateDisplayName(draft);
    if (!validated.ok) return; // canSave garantiert das; narrowt + defensiv.
    const nextName = validated.value;
    inflight.current = true;
    setSaving(true);
    setError(null);
    try {
      await api<{ user: ToolsProfileFormProps['user'] }>('/api/auth/me', {
        method: 'PATCH',
        body: JSON.stringify({ displayName: nextName }),
      });
      toast.success('Name aktualisiert');
      setDraft(nextName); // draft auf NFC angleichen → isDirty=false nach Save
      onSaved(nextName);
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Speichern fehlgeschlagen';
      setError(msg);
    } finally {
      inflight.current = false;
      setSaving(false);
    }
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
      {/* ── Konto ── */}
      <form onSubmit={handleSubmit}>
        <Card title="Konto" bodyClassName="p-5 space-y-5">
          <div>
            <label htmlFor="tools-profile-display-name" className={FIELD_LABEL_CLASS}>
              Anzeigename *
            </label>
            <input
              id="tools-profile-display-name"
              type="text"
              value={draft}
              onChange={(e) => {
                setDraft(e.target.value);
                if (error) setError(null);
              }}
              maxLength={DISPLAY_NAME_MAX}
              autoComplete="name"
              disabled={saving}
              className={FIELD_INPUT_CLASS}
            />
            <p className={FIELD_HINT_CLASS}>Max. {DISPLAY_NAME_MAX} Zeichen.</p>
          </div>

          {/* Read-only E-Mail + Rolle (zentral verwaltet). */}
          <div>
            <dl className="rounded-md bg-ink-50 px-4 py-3 space-y-2.5">
              <div className="flex items-baseline justify-between gap-3">
                <dt className="shrink-0 text-xs2 font-mono uppercase tracking-wider text-brand-green-600">
                  E-Mail
                </dt>
                <dd className="min-w-0 truncate text-sm text-brand-green-800">{user.email}</dd>
              </div>
              <div className="flex items-baseline justify-between gap-3">
                <dt className="shrink-0 text-xs2 font-mono uppercase tracking-wider text-brand-green-600">
                  Rolle
                </dt>
                <dd className="min-w-0 truncate text-sm text-brand-green-800">{user.role}</dd>
              </div>
            </dl>
            <p className={FIELD_HINT_CLASS}>E-Mail wird zentral verwaltet, die Rolle setzt ein Admin.</p>
          </div>

          {error && (
            <div
              role="alert"
              className="rounded-md bg-red-50 border border-red-100 px-3 py-2 text-sm text-red-700"
            >
              {error}
            </div>
          )}

          <div className="flex items-center gap-3 pt-1">
            <Button type="submit" variant="primary" loading={saving} disabled={!canSave}>
              {saving ? 'Speichert…' : 'Speichern'}
            </Button>
            {isDirty && !saving && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => {
                  setDraft(user.displayName);
                  setError(null);
                }}
              >
                Verwerfen
              </Button>
            )}
          </div>
        </Card>
      </form>

      {/* ── Passwort ändern (freiwillig, nicht-forced). ── */}
      <Card title="Passwort ändern" bodyClassName="p-5">
        <ChangePasswordForm />
      </Card>
    </div>
  );
}
